import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Users, IndianRupee, FileText, Receipt, Printer, Pencil, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import {
  getPatient,
  getPatientPrescriptions,
  getPatientInvoices,
  getSixMonthsAgoDate,
  queueWalkin,
  getBillingServices,
  createBillingInvoice,
  recordInvoicePayment,
  getDoctors,
} from '@/lib/api';
import PrintPrescriptionPreviewModal from '@/components/Prescription/PrintPrescriptionPreviewModal';
import PrintBillPreviewModal from '@/components/Billing/PrintBillPreviewModal';
import MedicalBillingModal from '@/components/Patient/MedicalBillingModal';
import { PaymentRecordModal } from '@/components/Billing/BillingModals';
import PatientFormModal from '@/components/Patient/PatientFormModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';

interface VisitPrescriptionRowProps {
  prescriptions: any[];
  onPrint: (rx: any) => void;
}

function VisitPrescriptionRow({ prescriptions, onPrint }: VisitPrescriptionRowProps) {
  const [expanded, setExpanded] = useState(false);
  const multiple = prescriptions.length > 1;
  const latest = prescriptions[0];

  return (
    <div className="mb-2">
      <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-background">
        <div className="flex items-center gap-3 min-w-0">
          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Prescription</span>
              {multiple && (
                <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
                  &times;{prescriptions.length}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {latest.doctor_name ? `Dr. ${latest.doctor_name}` : 'Doctor'}
              {latest.diagnosis && ` · ${latest.diagnosis}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {multiple && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-primary hover:underline"
            >
              {expanded ? 'Hide' : 'Show all'}
            </button>
          )}
          <button
            onClick={() => onPrint(latest)}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            title="Print latest prescription"
          >
            <Printer className="w-4 h-4" />
          </button>
        </div>
      </div>

      {multiple && expanded && (
        <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-3">
          {prescriptions.map((rx, i) => (
            <div key={rx.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-muted/50">
              <div>
                <p className="text-xs font-medium">Prescription {i + 1}</p>
                <p className="text-xs text-muted-foreground">
                  {rx.diagnosis || (rx.medicines?.length ? `${rx.medicines.length} medicines` : 'No details')}
                </p>
              </div>
              <button
                onClick={() => onPrint(rx)}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                <Printer className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function FrontDeskPatientPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [patient, setPatient] = useState<any>(null);
  const [prescriptions, setPrescriptions] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [printRx, setPrintRx] = useState<any>(null);
  const [printInvoice, setPrintInvoice] = useState<any>(null);

  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingServices, setBillingServices] = useState<any[]>([]);
  const [billingLoading, setBillingLoading] = useState(false);

  const [collectInvoice, setCollectInvoice] = useState<any>(null);

  const [doctors, setDoctors] = useState<{id: string, name: string}[]>([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState<string | null>(null);
  const [queueLoading, setQueueLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBookModal, setShowBookModal] = useState(false);

  useEffect(() => {
    if (!patientId) return;
    loadAll();
  }, [patientId]);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const from = getSixMonthsAgoDate();
      const [patientData, rxData, invData] = await Promise.all([
        getPatient(patientId!),
        getPatientPrescriptions(patientId!, { from }),
        getPatientInvoices(patientId!, { from }),
      ]);

      if (!patientData) {
        setError('Patient not found');
        return;
      }

      setPatient(patientData);
      setPrescriptions(rxData);
      setInvoices(invData);

      const doctorsData = await getDoctors();
      if (doctorsData && doctorsData.length > 0) {
        setDoctors(doctorsData);
        setSelectedDoctorId(prev => prev ?? doctorsData[0].id);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error
        ? err.message : 'Failed to load patient data';
      setError(msg);
      toast({
        title: 'Error',
        description: msg,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToQueue = async () => {
    if (!selectedDoctorId) {
      toast({
        title: 'No doctor selected',
        description: 'Please select a doctor first',
        variant: 'destructive'
      });
      return;
    }
    setQueueLoading(true);
    try {
      await queueWalkin(
        patientId!,
        selectedDoctorId,
        currentUser!.clinic_id!,
        false
      );
      toast({
        title: 'Added to queue',
        description: `${patient.name} added to queue`
      });
    } catch (err: unknown) {
      const msg = err instanceof Error
        ? err.message : 'Failed to add to queue';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setQueueLoading(false);
    }
  };

  const handleNewInvoice = async () => {
    setBillingLoading(true);
    try {
      const services = await getBillingServices(
        currentUser!.clinic_id!,
        currentUser?.doctor_id
      );
      setBillingServices(
        Array.isArray(services)
          ? services
          : (services as any)?.services ?? []
      );
    } catch {
      setBillingServices([]);
    } finally {
      setBillingLoading(false);
    }
    setShowBillingModal(true);
  };

  const handleCreateInvoice = async (invoiceData: any) => {
    try {
      await createBillingInvoice({
        patient_id: patient.id,
        patient_name: patient.name,
        patient_phone: patient.phone,
        doctor_id: selectedDoctorId ?? currentUser?.doctor_id ?? '',
        clinic_id: currentUser!.clinic_id!,
        items: invoiceData.items,
        discount_amount: invoiceData.discount || 0,
        payment_method: invoiceData.paymentMethod,
        paid_amount: invoiceData.paidAmount ?? invoiceData.finalTotal,
      });
      toast({
        title: 'Invoice created',
        description: `Bill created for ${patient.name}`
      });
      setShowBillingModal(false);
      const from = getSixMonthsAgoDate();
      const invData = await getPatientInvoices(patientId!, { from });
      setInvoices(invData);
    } catch (err: unknown) {
      const msg = err instanceof Error
        ? err.message : 'Failed to create invoice';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const handleCollectPayment = async (amount: number, method: string) => {
    if (!collectInvoice) return;
    try {
      await recordInvoicePayment(collectInvoice.id, {
        amount,
        payment_method: method,
        payment_date: new Date().toISOString(),
      });
      toast({
        title: 'Payment recorded',
        description: `₹${amount} collected`
      });
      setCollectInvoice(null);
      const from = getSixMonthsAgoDate();
      const invData = await getPatientInvoices(patientId!, { from });
      setInvoices(invData);
    } catch (err: unknown) {
      const msg = err instanceof Error
        ? err.message : 'Failed to record payment';
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    }
  };

  const groupedVisits = React.useMemo(() => {
    const map: Record<string, { date: string; prescriptions: any[]; invoices: any[] }> = {};

    prescriptions.forEach(rx => {
      const date = (rx.date || rx.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].prescriptions.push(rx);
    });

    invoices.forEach(inv => {
      const date = (inv.date || inv.created_at || '').split('T')[0];
      if (!map[date]) map[date] = { date, prescriptions: [], invoices: [] };
      map[date].invoices.push(inv);
    });

    return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
  }, [prescriptions, invoices]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            Loading patient...
          </p>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="p-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/frontdesk')}
          className="gap-2 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Front Desk
        </Button>
        <div className="text-center py-12">
          <p className="text-destructive font-medium">{error}</p>
          <Button
            variant="outline"
            onClick={loadAll}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 max-w-4xl mx-auto">
      {/* Sticky Header */}
      <div className="sticky top-[57px] z-10 bg-background border-b border-border pb-4 pt-4 px-6 -mx-6">
        <div className="flex items-center gap-3 mb-1">
          <button
            onClick={() => navigate('/frontdesk')}
            className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-xl font-bold text-foreground capitalize">
            {patient.name}
          </h1>
        </div>

        <div className="flex items-center gap-2 ml-9 mb-3 text-sm text-muted-foreground flex-wrap">
          {patient.age && <span>{patient.age}y</span>}
          {patient.age && patient.gender && <span>&middot;</span>}
          {patient.gender && <span>{patient.gender}</span>}
          {patient.phone && (
            <>
              <span>&middot;</span>
              <span>{patient.phone}</span>
            </>
          )}
          {patient.mrn && (
            <>
              <span>&middot;</span>
              <span className="text-xs bg-muted px-2 py-0.5 rounded-full font-medium">
                MRN: {patient.mrn}
              </span>
            </>
          )}
          {patient.last_visit_date && (
            <>
              <span>&middot;</span>
              <span className="text-xs">
                Last visit:{' '}
                {new Date(patient.last_visit_date).toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric'
                })}
              </span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 ml-9">
          {doctors.length > 1 && (
            <select
              value={selectedDoctorId ?? ''}
              onChange={e => setSelectedDoctorId(e.target.value)}
              className="text-xs border border-input rounded-lg px-2 py-1.5 bg-background h-8"
            >
              {doctors.map(d => (
                <option key={d.id} value={d.id}>
                  {d.name.startsWith('Dr.') ? d.name : `Dr. ${d.name}`}
                </option>
              ))}
            </select>
          )}
          <Button
            size="sm"
            onClick={handleAddToQueue}
            disabled={queueLoading}
            className="gap-1.5 h-8"
          >
            <Users className="w-3.5 h-3.5" />
            {queueLoading ? 'Adding...' : 'Add to Queue'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleNewInvoice}
            className="gap-1.5 h-8"
          >
            <IndianRupee className="w-3.5 h-3.5" />
            New Invoice
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowBookModal(true)}
            className="gap-1.5 h-8"
          >
            <Calendar className="w-3.5 h-3.5" />
            Book Appointment
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setShowEditModal(true)}
            className="gap-1.5 h-8 text-muted-foreground"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Button>
        </div>
      </div>

      {/* Date-Grouped Visit History */}
      <div className="pt-4 space-y-4">
        {groupedVisits.length === 0 && (
          <div className="text-center py-16">
            <FileText className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">
              No visits in last 6 months
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">
              Add to queue or create an invoice to get started
            </p>
          </div>
        )}

        {groupedVisits.map((visit, visitIndex) => (
          <div key={visit.date}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-muted-foreground">
                {new Date(visit.date + 'T00:00:00').toLocaleDateString('en-IN', {
                  day: 'numeric', month: 'short', year: 'numeric'
                })}
              </span>
              {visitIndex === 0 && (
                <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-medium">
                  Latest
                </span>
              )}
            </div>

            {visit.prescriptions.length > 0 && (
              <VisitPrescriptionRow
                prescriptions={visit.prescriptions}
                onPrint={setPrintRx}
              />
            )}

            {visit.invoices.map(inv => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-lg border border-border bg-background mb-2 last:mb-0">
                <div className="flex items-center gap-3 min-w-0">
                  <Receipt className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">₹{inv.total_amount}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                        inv.payment_status === 'Paid'
                          ? 'bg-success/10 text-success'
                          : inv.payment_status === 'Partial'
                          ? 'bg-warning/10 text-warning'
                          : 'bg-destructive/10 text-destructive'
                      }`}>
                        {inv.payment_status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">{inv.invoice_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(inv.payment_status === 'Pending' || inv.payment_status === 'Partial') && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCollectInvoice(inv)}
                      className="h-7 text-xs gap-1 text-warning border-warning/30 hover:bg-warning/10"
                    >
                      Collect
                    </Button>
                  )}
                  <button
                    onClick={() => setPrintInvoice(inv)}
                    className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title="Print invoice"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Print Prescription */}
      {printRx && (
        <PrintPrescriptionPreviewModal
          open={!!printRx}
          prescription={printRx}
          onClose={() => setPrintRx(null)}
        />
      )}

      {/* Print Invoice */}
      {printInvoice && (
        <PrintBillPreviewModal
          open={!!printInvoice}
          invoice={printInvoice}
          onClose={() => setPrintInvoice(null)}
        />
      )}

      {/* New Invoice */}
      {showBillingModal && (
        <MedicalBillingModal
          isOpen={showBillingModal}
          onClose={() => setShowBillingModal(false)}
          patient={patient}
          services={billingServices}
          onCreateInvoice={handleCreateInvoice}
          loading={billingLoading}
          clinicId={currentUser!.clinic_id!}
          doctorId={selectedDoctorId ?? currentUser?.doctor_id ?? ''}
          onRefreshServices={async () => {
            const s = await getBillingServices(currentUser!.clinic_id!);
            setBillingServices(
              Array.isArray(s) ? s : (s as any)?.services ?? []
            );
          }}
        />
      )}

      {/* Collect Payment */}
      <PaymentRecordModal
        isOpen={!!collectInvoice}
        onClose={() => setCollectInvoice(null)}
        invoice={collectInvoice}
        onRecordPayment={handleCollectPayment}
        loading={false}
      />

      {/* Edit Patient */}
      <PatientFormModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={async () => {
          setShowEditModal(false);
          const updated = await getPatient(patientId!);
          if (updated) setPatient(updated);
        }}
        mode="edit"
        patientId={patient?.id}
        initialData={patient ? {
          name: patient.name ?? '',
          age: patient.age?.toString() ?? '',
          gender: patient.gender ?? '',
          phone: patient.phone ?? '',
          phone_2: patient.phone_2 ?? '',
          email: patient.email ?? '',
          address: patient.address ?? '',
          city: patient.city ?? '',
          pin_code: patient.pin_code ?? '',
          blood_group: patient.blood_group ?? '',
          allergies: patient.allergies ?? '',
          date_of_birth: patient.date_of_birth ?? '',
          preferred_language: patient.preferred_language ?? 'English',
          aadhar_number: patient.aadhar_number ?? '',
          emergency_contact_name: patient.emergency_contact_name ?? '',
          emergency_contact_phone: patient.emergency_contact_phone ?? '',
          payment_preference: patient.payment_preference ?? 'Cash',
        } : undefined}
        showPrescriptionOption={false}
        showQueueOption={false}
        title="Edit Patient"
      />

      {/* Book Appointment */}
      <AddAppointmentModal
        isOpen={showBookModal}
        onClose={() => setShowBookModal(false)}
        patientId={patient?.id}
        patients={patient ? [patient] : []}
        showPatientSelection={false}
        onPatientSearch={() => {}}
        onAdded={() => {
          setShowBookModal(false);
          toast({
            title: 'Appointment booked',
            description: 'Appointment scheduled successfully'
          });
        }}
      />
    </div>
  );
}
